import React, { useState } from 'react';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem',
  padding: '2rem 0'
});

// 页面标题区块
const TitleSection = styled('section', {
  textAlign: 'center',
  marginBottom: '2rem'
});

// 标题
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto',
  color: '$textSecondary',
  lineHeight: 1.6
});

// FAQ容器
const FaqContainer = styled('div', {
  maxWidth: '900px',
  margin: '0 auto',
  width: '100%'
});

// FAQ类别标题
const CategoryTitle = styled('h2', {
  fontSize: '1.75rem',
  fontWeight: 'bold',
  marginBottom: '1.5rem',
  marginTop: '2rem',
  '&:first-of-type': {
    marginTop: 0
  }
});

// FAQ项目
const FaqItem = styled('div', {
  marginBottom: '1rem',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  backgroundColor: '$backgroundSecondary'
});

// FAQ问题按钮
const FaqQuestion = styled('button', {
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '1.25rem 1.5rem',
  cursor: 'pointer',
  border: 'none',
  background: 'transparent',
  color: '$textPrimary',
  textAlign: 'left',
  fontSize: '1.1rem',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '$backgroundHover'
  }
});

// 箭头图标
const ArrowIcon = styled('span', {
  fontSize: '1rem',
  transition: 'transform 0.3s ease',
  variants: {
    open: {
      true: {
        transform: 'rotate(180deg)'
      }
    }
  }
});

// FAQ回答容器
const FaqAnswer = styled('div', {
  padding: '0 1.5rem',
  height: 0,
  overflow: 'hidden',
  transition: 'height 0.3s ease, padding 0.3s ease',
  color: '$textSecondary',
  variants: {
    open: {
      true: {
        padding: '0 1.5rem 1.5rem 1.5rem',
        height: 'auto'
      }
    }
  }
});

// FAQ回答内容
const AnswerContent = styled('div', {
  fontSize: '1rem',
  lineHeight: 1.6
});

// 联系支持部分
const ContactSupport = styled('div', {
  marginTop: '3rem',
  padding: '2rem',
  backgroundColor: '$backgroundSecondary',
  borderRadius: '16px',
  textAlign: 'center'
});

// 联系标题
const ContactTitle = styled('h2', {
  fontSize: '1.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem'
});

// 联系说明
const ContactDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  maxWidth: '600px',
  margin: '0 auto 1.5rem',
  lineHeight: 1.6
});

// 联系按钮
const ContactButton = styled('a', {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 2rem',
  backgroundColor: '$primary',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryDark'
  }
});

/**
 * 矿机托管常见问题组件
 * 展示与矿机托管相关的常见问题及解答
 */
const MiningHotelFaq: React.FC = () => {
  const { t } = useTranslation();
  const [openItems, setOpenItems] = useState<Record<string, boolean>>({});
  
  // 切换FAQ问题开关状态
  const toggleItem = (id: string) => {
    setOpenItems((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };
  
  // FAQ数据结构
  const faqCategories = [
    {
      id: 'general',
      title: t('miningHotel.faq.categories.general'),
      items: [
        {
          id: 'what-is-mining-hotel',
          question: t('miningHotel.faq.whatIsMiningHotel.question'),
          answer: t('miningHotel.faq.whatIsMiningHotel.answer')
        },
        {
          id: 'benefits',
          question: t('miningHotel.faq.benefits.question'),
          answer: t('miningHotel.faq.benefits.answer')
        },
        {
          id: 'location',
          question: t('miningHotel.faq.location.question'),
          answer: t('miningHotel.faq.location.answer')
        },
        {
          id: 'security',
          question: t('miningHotel.faq.security.question'),
          answer: t('miningHotel.faq.security.answer')
        }
      ]
    },
    {
      id: 'pricing',
      title: t('miningHotel.faq.categories.pricing'),
      items: [
        {
          id: 'fee-structure',
          question: t('miningHotel.faq.feeStructure.question'),
          answer: t('miningHotel.faq.feeStructure.answer')
        },
        {
          id: 'electricity-cost',
          question: t('miningHotel.faq.electricityCost.question'),
          answer: t('miningHotel.faq.electricityCost.answer')
        },
        {
          id: 'minimum-contract',
          question: t('miningHotel.faq.minimumContract.question'),
          answer: t('miningHotel.faq.minimumContract.answer')
        },
        {
          id: 'payment-methods',
          question: t('miningHotel.faq.paymentMethods.question'),
          answer: t('miningHotel.faq.paymentMethods.answer')
        }
      ]
    },
    {
      id: 'technical',
      title: t('miningHotel.faq.categories.technical'),
      items: [
        {
          id: 'supported-miners',
          question: t('miningHotel.faq.supportedMiners.question'),
          answer: t('miningHotel.faq.supportedMiners.answer')
        },
        {
          id: 'setup-process',
          question: t('miningHotel.faq.setupProcess.question'),
          answer: t('miningHotel.faq.setupProcess.answer')
        },
        {
          id: 'cooling-system',
          question: t('miningHotel.faq.coolingSystem.question'),
          answer: t('miningHotel.faq.coolingSystem.answer')
        },
        {
          id: 'monitoring',
          question: t('miningHotel.faq.monitoring.question'),
          answer: t('miningHotel.faq.monitoring.answer')
        },
        {
          id: 'maintenance',
          question: t('miningHotel.faq.maintenance.question'),
          answer: t('miningHotel.faq.maintenance.answer')
        }
      ]
    },
    {
      id: 'operations',
      title: t('miningHotel.faq.categories.operations'),
      items: [
        {
          id: 'uptime',
          question: t('miningHotel.faq.uptime.question'),
          answer: t('miningHotel.faq.uptime.answer')
        },
        {
          id: 'shipping',
          question: t('miningHotel.faq.shipping.question'),
          answer: t('miningHotel.faq.shipping.answer')
        },
        {
          id: 'visit',
          question: t('miningHotel.faq.visit.question'),
          answer: t('miningHotel.faq.visit.answer')
        },
        {
          id: 'withdrawal',
          question: t('miningHotel.faq.withdrawal.question'),
          answer: t('miningHotel.faq.withdrawal.answer')
        }
      ]
    }
  ];
  
  // 计算回答高度，用于动画
  const getAnswerHeight = (id: string, ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return 'auto';
    return openItems[id] ? `${ref.current.scrollHeight}px` : '0';
  };
  
  return (
    <PageContainer>
      <TitleSection>
        <Title>{t('miningHotel.faq.title')}</Title>
        <Subtitle>{t('miningHotel.faq.subtitle')}</Subtitle>
      </TitleSection>
      
      <FaqContainer>
        {faqCategories.map((category) => (
          <React.Fragment key={category.id}>
            <CategoryTitle>{category.title}</CategoryTitle>
            {category.items.map((item) => {
              const answerRef = React.createRef<HTMLDivElement>();
              return (
                <FaqItem key={item.id}>
                  <FaqQuestion
                    onClick={() => toggleItem(item.id)}
                    aria-expanded={openItems[item.id]}
                    aria-controls={`answer-${item.id}`}
                  >
                    {item.question}
                    <ArrowIcon open={openItems[item.id]}>▼</ArrowIcon>
                  </FaqQuestion>
                  <FaqAnswer
                    id={`answer-${item.id}`}
                    open={openItems[item.id]}
                    role="region"
                    style={{ height: openItems[item.id] ? getAnswerHeight(item.id, answerRef) : '0' }}
                  >
                    <AnswerContent ref={answerRef as React.RefObject<HTMLDivElement>}>
                      {item.answer}
                    </AnswerContent>
                  </FaqAnswer>
                </FaqItem>
              );
            })}
          </React.Fragment>
        ))}
      </FaqContainer>
      
      <ContactSupport>
        <ContactTitle>{t('miningHotel.faq.stillHaveQuestions')}</ContactTitle>
        <ContactDescription>{t('miningHotel.faq.contactDescription')}</ContactDescription>
        <ContactButton href="/contact">{t('miningHotel.faq.contactSupport')}</ContactButton>
      </ContactSupport>
    </PageContainer>
  );
};

export default MiningHotelFaq; 